import { TriangleAlert } from 'lucide-react';
import PropTypes from 'prop-types';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import styles from './NoticeIcon.module.scss';

const TIP_GAP = 6;
const TIP_EDGE = 8;

export const NoticeIcon = ({
  tone = 'warning',
  icon: Icon = TriangleAlert,
  count,
  label,
  detail,
  onClick,
}) => {
  const iconRef = useRef(null);
  const tipRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState(null);

  const show = () => setOpen(true);
  const hide = () => {
    setOpen(false);
    setPosition(null);
  };

  useLayoutEffect(() => {
    if (!open || !iconRef.current || !tipRef.current) return;
    const icon = iconRef.current.getBoundingClientRect();
    const tip = tipRef.current.getBoundingClientRect();
    const fitsBelow = icon.bottom + TIP_GAP + tip.height <= window.innerHeight - TIP_EDGE;
    const top = fitsBelow ? icon.bottom + TIP_GAP : icon.top - TIP_GAP - tip.height;
    const left = Math.max(
      TIP_EDGE,
      Math.min(icon.right - tip.width, window.innerWidth - tip.width - TIP_EDGE),
    );
    setPosition({ top, left });
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    window.addEventListener('scroll', hide, true);
    return () => window.removeEventListener('scroll', hide, true);
  }, [open]);

  const className = `${styles.noticeIcon} ${styles[tone]}`;
  const content = (
    <>
      <Icon size={13} aria-hidden="true" />
      {count > 0 && (
        <span className={styles.count} aria-hidden="true">
          {count}
        </span>
      )}
    </>
  );
  const hoverProps = { onMouseEnter: show, onMouseLeave: hide, onFocus: show, onBlur: hide };

  return (
    <>
      {onClick ? (
        <span
          ref={iconRef}
          role="button"
          tabIndex={0}
          aria-label={label}
          className={className}
          onClick={(event) => {
            event.stopPropagation();
            onClick();
          }}
          onKeyDown={(event) => {
            if (event.key !== 'Enter' && event.key !== ' ') return;
            event.preventDefault();
            event.stopPropagation();
            onClick();
          }}
          {...hoverProps}
        >
          {content}
        </span>
      ) : (
        <span ref={iconRef} role="img" aria-label={label} className={className} {...hoverProps}>
          {content}
        </span>
      )}
      {open &&
        createPortal(
          <div
            ref={tipRef}
            role="tooltip"
            className={styles.tip}
            style={{
              top: position?.top ?? 0,
              left: position?.left ?? 0,
              visibility: position ? 'visible' : 'hidden',
            }}
          >
            <strong>{label}</strong>
            {detail && <div className={styles.tipDetail}>{detail}</div>}
          </div>,
          document.body,
        )}
    </>
  );
};

NoticeIcon.propTypes = {
  tone: PropTypes.oneOf(['neutral', 'warning', 'planned', 'alert']),
  icon: PropTypes.elementType,
  count: PropTypes.number,
  label: PropTypes.string.isRequired,
  detail: PropTypes.string,
  onClick: PropTypes.func,
};

export const NoticeMark = (props) => <NoticeIcon {...props} onClick={undefined} />;

NoticeMark.propTypes = NoticeIcon.propTypes;

export default NoticeIcon;
